import localforage from 'localforage';
import { Vessel } from '@app-types/index';

class LocalStorageService {
  private store: LocalForage;

  constructor() {
    this.store = localforage.createInstance({
      name: 'verifyvesselcertificate',
      storeName: 'vessels',
    });
  }

  /**
   * Save a single vessel to local storage
   */
  async save(vessel: Vessel): Promise<Vessel> {
    try {
      await this.store.setItem(vessel.vesselID.toString(), vessel);
      return vessel;
    } catch (error) {
      console.error('Error saving vessel:', error);
      throw error;
    }
  }

  /**
   * Save multiple vessels
   */
  async saveAll(vessels: Vessel[]): Promise<void> {
    try {
      await Promise.all(vessels.map((vessel) => this.save(vessel)));
    } catch (error) {
      console.error('Error saving vessels:', error);
      throw error;
    }
  }

  /**
   * Get a single vessel by ID
   */
  async get(vesselID: string | number): Promise<Vessel | null> {
    try {
      const vessel = await this.store.getItem<Vessel>(vesselID.toString());
      return vessel || null;
    } catch (error) {
      console.error('Error getting vessel:', error);
      throw error;
    }
  }

  /**
   * Get all vessels
   */
  async getAll(): Promise<Vessel[]> {
    try {
      const vessels: Vessel[] = [];
      await this.store.iterate<Vessel, void>((value) => {
        vessels.push(value);
      });
      return vessels;
    } catch (error) {
      console.error('Error getting all vessels:', error);
      throw error;
    }
  }

  /**
   * Remove a single vessel by ID
   */
  async remove(vesselID: string | number): Promise<void> {
    try {
      await this.store.removeItem(vesselID.toString());
    } catch (error) {
      console.error('Error removing vessel:', error);
      throw error;
    }
  }

  /**
   * Clear all vessels
   */
  async removeAll(): Promise<void> {
    try {
      await this.store.clear();
    } catch (error) {
      console.error('Error clearing all vessels:', error);
      throw error;
    }
  }

  /**
   * Get the number of vessels
   */
  async count(): Promise<number> {
    try {
      return await this.store.length();
    } catch (error) {
      console.error('Error getting count:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const localStorageService = new LocalStorageService();
export default LocalStorageService;
